import { DEFAULT_DATE_FORMAT, DEFAULT_ISO_DATE_FORMAT } from '@shared/utils'
import { DatePicker, DatePickerProps } from 'antd'
import dayjs from 'dayjs'
import { useCallback } from 'react'

export const InputDatePicker = ({
  value,
  onChange,
  ...props
}: DatePickerProps) => {
  const formatInput = useCallback((input: string) => {
    const numbers = input.replace(/\D/g, '')

    if (numbers.length <= 2) {
      return numbers
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}.${numbers.slice(2)}`
    } else if (numbers.length <= 8) {
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 4)}.${numbers.slice(4)}`
    } else {
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 4)}.${numbers.slice(
        4,
        8
      )}`
    }
  }, [])

  const parseDate = useCallback((dateString: string) => {
    const parts = dateString?.split('.')?.map((p) => p?.trim())
    if (parts?.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)

      if (
        Number.isFinite(day) &&
        Number.isFinite(month) &&
        Number.isFinite(year) &&
        day >= 1 &&
        day <= 31 &&
        month >= 1 &&
        month <= 12 &&
        year >= 1900
      ) {
        const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(
          2,
          '0'
        )}-${String(day).padStart(2, '0')}`

        const parsed = dayjs?.(iso, DEFAULT_ISO_DATE_FORMAT, true)
        if (parsed?.isValid?.()) return parsed
      }
    }
    return null
  }, [])

  const onChangeDate = (
    date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    if (!date && typeof dateString === 'string') {
      const parsed = parseDate?.(dateString)

      onChange?.(
        parsed as dayjs.Dayjs,
        parsed?.format?.(DEFAULT_DATE_FORMAT) || ''
      )
      return
    }
    if (date) {
      onChange?.(date, dateString as string)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement
    const currentValue = target.value

    if (e.key === 'Enter') {
      e.preventDefault()
      const parsed = parseDate?.(currentValue)
      if (parsed) {
        onChange?.(parsed, parsed.format?.(DEFAULT_DATE_FORMAT))

        target.blur()
      }
      return
    }

    if (/\d/.test(e.key)) {
      e.preventDefault()

      const selectionStart = target.selectionStart ?? currentValue.length
      const selectionEnd = target.selectionEnd ?? selectionStart

      const raw = `${currentValue.slice(0, selectionStart)}${
        e.key
      }${currentValue.slice(selectionEnd)}`

      const formatted = formatInput(raw)
      target.value = formatted

      target.setSelectionRange(formatted.length, formatted.length)

      const ev = new Event('input', { bubbles: true })
      target.dispatchEvent(ev)
    }
  }

  const onBlur = (e: React.FocusEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement
    const parsed = parseDate(target.value)

    if (parsed) {
      onChange?.(parsed, parsed.format?.(DEFAULT_DATE_FORMAT))
    }
  }

  return (
    <DatePicker
      className="w-full"
      {...props}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      format={DEFAULT_DATE_FORMAT}
      value={dayjs?.isDayjs?.(value) ? value : null}
      onChange={onChangeDate}
    />
  )
}
